import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { AuthResponse, UserPublic } from '@xingroute/schema'
import { ApiError, api, subscribeAuthSession } from '../api/client.js'

interface AuthState {
  status: 'loading' | 'guest' | 'authed'
  user: UserPublic | null
  token: string | null
}

interface AuthApi extends AuthState {
  register: (username: string, password: string, displayName?: string) => Promise<void>
  login: (username: string, password: string) => Promise<void>
  applyAuthResponse: (response: AuthResponse) => void
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthApi | null>(null)

// localStorage hint flag: a non-sensitive marker that the current browser has
// previously held a refresh-cookie session. We can't read the httpOnly refresh
// cookie itself, so this flag is the only way to skip the bootstrap call to
// /auth/refresh for first-time / logged-out visitors. Without it, every
// anonymous /discover or /r/:slug page-load triggers a 401 console error.
// Set on successful login/register/refresh; cleared on logout or 401.
const SESSION_HINT_KEY = 'xr.session.hint'

function readSessionHint(): boolean {
  try {
    return globalThis.localStorage?.getItem(SESSION_HINT_KEY) === '1'
  } catch {
    return false
  }
}

function writeSessionHint(present: boolean): void {
  try {
    if (present) globalThis.localStorage?.setItem(SESSION_HINT_KEY, '1')
    else globalThis.localStorage?.removeItem(SESSION_HINT_KEY)
  } catch {
    /* ignore quota / disabled storage */
  }
}

function isSameAuthedSession(state: AuthState, token: string, userId: string): boolean {
  return state.status === 'authed' && state.token === token && state.user?.id === userId
}

function hasAuthToken(state: AuthState, token: string): boolean {
  return state.status === 'authed' && state.token === token
}

function canApplyRefreshResult(started: AuthState, current: AuthState): boolean {
  if (started.status === 'loading') return current.status === 'loading'
  const token = started.token
  const userId = started.user?.id
  if (started.status === 'authed' && token && userId) {
    return isSameAuthedSession(current, token, userId)
  }
  return current.status === started.status
}

function isAuthIdentityReplacement(started: AuthState, nextUserId: string): boolean {
  return started.status === 'authed' && started.user?.id != null && started.user.id !== nextUserId
}

let authIdentityReplacementReloader = () => globalThis.location.reload()

export function __setAuthIdentityReplacementReloaderForTests(fn: (() => void) | null): void {
  authIdentityReplacementReloader = fn ?? (() => globalThis.location.reload())
}

function reloadForAuthIdentityReplacement(): void {
  console.warn('[AuthProvider] refreshed auth identity changed; reloading app')
  authIdentityReplacementReloader()
}

export function useAuth(): AuthApi {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setReactState] = useState<AuthState>(() =>
    readSessionHint()
      ? { status: 'loading', user: null, token: null }
      : { status: 'guest', user: null, token: null },
  )
  const stateRef = useRef(state)
  const setAuthState = useCallback((next: AuthState | ((prev: AuthState) => AuthState)) => {
    const resolved = typeof next === 'function' ? next(stateRef.current) : next
    stateRef.current = resolved
    setReactState(resolved)
  }, [])

  useEffect(() => {
    stateRef.current = state
  }, [state])

  const refresh = useCallback(async () => {
    const started = stateRef.current
    try {
      const res = await api.refresh()
      const current = stateRef.current
      if (!canApplyRefreshResult(started, current)) return
      writeSessionHint(true)
      if (isAuthIdentityReplacement(started, res.user.id)) {
        reloadForAuthIdentityReplacement()
        return
      }
      setAuthState({ status: 'authed', user: res.user, token: res.access_token })
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 401)) {
        setAuthState((prev) =>
          canApplyRefreshResult(started, prev)
            ? { status: 'guest', user: null, token: null }
            : prev,
        )
        return
      }
      let applied = false
      setAuthState((prev) => {
        if (!canApplyRefreshResult(started, prev)) return prev
        applied = true
        return { status: 'guest', user: null, token: null }
      })
      if (applied) writeSessionHint(false)
    }
  }, [setAuthState])

  const applyAuthResponse = useCallback(
    (response: AuthResponse) => {
      writeSessionHint(true)
      setAuthState({ status: 'authed', user: response.user, token: response.access_token })
    },
    [setAuthState],
  )

  const refreshCurrentUser = useCallback(async () => {
    const current = stateRef.current
    const token = current.token
    const userId = current.user?.id
    if (current.status !== 'authed' || !token || !userId) return

    try {
      const user = await api.me(token, { skipAuthRefresh: true })
      if (user.id !== userId) {
        if (isSameAuthedSession(stateRef.current, token, userId)) {
          writeSessionHint(true)
          reloadForAuthIdentityReplacement()
        }
        return
      }
      setAuthState((prev) => (isSameAuthedSession(prev, token, userId) ? { ...prev, user } : prev))
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 401)) {
        console.warn('[AuthProvider] current user refresh failed', err)
        return
      }

      if (!isSameAuthedSession(stateRef.current, token, userId)) return
      try {
        const res = await api.refresh()
        if (!isSameAuthedSession(stateRef.current, token, userId)) return
        writeSessionHint(true)
        if (res.user.id !== userId) {
          reloadForAuthIdentityReplacement()
          return
        }
        setAuthState({ status: 'authed', user: res.user, token: res.access_token })
      } catch (refreshErr) {
        if (refreshErr instanceof ApiError && refreshErr.status === 401) {
          if (isSameAuthedSession(stateRef.current, token, userId)) {
            writeSessionHint(false)
            setAuthState({ status: 'guest', user: null, token: null })
          }
          return
        }
        console.warn('[AuthProvider] current user refresh failed', refreshErr)
      }
    }
  }, [setAuthState])

  useEffect(() => {
    if (readSessionHint()) {
      void refresh()
    }
  }, [refresh])

  // App→browser edit handoff: iOS opens the mobile browser with a pre-confirmed
  // QR login challenge (`login_challenge` + `login_secret`) so the user lands
  // logged-in without scanning a QR. Poll-consume it once on mount, adopt the
  // session (the poll endpoint also sets the refresh cookie), then strip the
  // one-time params from the URL so they never linger in history / referrer.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const challengeId = params.get('login_challenge')
    const secret = params.get('login_secret')
    if (!challengeId || !secret) return
    let cancelled = false
    void (async () => {
      try {
        const res = await api.pollQrLoginChallenge(challengeId, secret)
        if (!cancelled && res.status === 'confirmed') {
          applyAuthResponse(res.auth)
        }
      } catch {
        // Ignore — challenge expired / already consumed / invalid; the user can
        // still sign in manually. The params are stripped below regardless.
      } finally {
        params.delete('login_challenge')
        params.delete('login_secret')
        const qs = params.toString()
        window.history.replaceState(
          null,
          '',
          window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash,
        )
      }
    })()
    return () => {
      cancelled = true
    }
  }, [applyAuthResponse])

  useEffect(
    () =>
      subscribeAuthSession((session, originToken) => {
        if (originToken && !hasAuthToken(stateRef.current, originToken)) return
        if (!session) {
          writeSessionHint(false)
          setAuthState({ status: 'guest', user: null, token: null })
          return
        }
        writeSessionHint(true)
        const current = stateRef.current
        if (
          originToken &&
          current.status === 'authed' &&
          current.user?.id != null &&
          current.user.id !== session.user.id
        ) {
          reloadForAuthIdentityReplacement()
          return
        }
        setAuthState({ status: 'authed', user: session.user, token: session.access_token })
      }),
    [setAuthState],
  )

  useEffect(() => {
    let inFlight = false
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'hidden' || inFlight) return
      inFlight = true
      void refreshCurrentUser().finally(() => {
        inFlight = false
      })
    }

    window.addEventListener('focus', refreshWhenVisible)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.removeEventListener('focus', refreshWhenVisible)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [refreshCurrentUser])

  const value = useMemo<AuthApi>(
    () => ({
      ...state,
      async register(username, password, displayName) {
        const res = await api.register(username, password, displayName)
        writeSessionHint(true)
        setAuthState({ status: 'authed', user: res.user, token: res.access_token })
      },
      async login(username, password) {
        const res = await api.login(username, password)
        writeSessionHint(true)
        setAuthState({ status: 'authed', user: res.user, token: res.access_token })
      },
      applyAuthResponse,
      async logout() {
        try {
          await api.logout()
        } finally {
          writeSessionHint(false)
          setAuthState({ status: 'guest', user: null, token: null })
        }
      },
      refresh,
    }),
    [applyAuthResponse, refresh, setAuthState, state],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
